import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { MapPin, RefreshCw, Building2, Phone, Mail, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { logError } from "@/lib/logger";
import { Button } from "@/components/ui/button";

const PIN = (color) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${color};width:20px;height:20px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,.35)"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 20],
  });

export default function PartnersMap() {
  const { t } = useI18n();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const mapRef = useRef(null);
  const map = useRef(null);
  const layer = useRef(null);
  const markers = useRef({});

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await api.get(`/partners/map${refresh ? "?refresh=true" : ""}`);
      setPartners(data || []);
    } catch (e) {
      logError("partners-map:load", e);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (map.current || !mapRef.current) return;
    map.current = L.map(mapRef.current, { zoomControl: true, attributionControl: true, scrollWheelZoom: true })
      .setView([42.5, 12.5], 5.4);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19, attribution: "&copy; OpenStreetMap" }).addTo(map.current);
    layer.current = L.layerGroup().addTo(map.current);
    return () => { if (map.current) { map.current.remove(); map.current = null; } };
  }, []);

  useEffect(() => {
    if (!map.current || !layer.current) return;
    layer.current.clearLayers();
    markers.current = {};
    const pts = [];
    partners.filter((p) => p.located).forEach((p) => {
      const m = L.marker([p.lat, p.lng], { icon: PIN("#1e3a8a") }).addTo(layer.current);
      m.bindPopup(
        `<div style="font-family:system-ui;min-width:170px">
           <div style="font-weight:700;color:#0f172a">${p.name || "—"}</div>
           <div style="color:#475569;font-size:12px;margin-top:2px">${[p.city, p.region].filter(Boolean).join(" · ")}</div>
           ${p.contact_name ? `<div style="color:#64748b;font-size:12px;margin-top:4px">${p.contact_name}</div>` : ""}
           ${p.email ? `<div style="color:#64748b;font-size:12px">${p.email}</div>` : ""}
         </div>`);
      markers.current[p.id] = m;
      pts.push([p.lat, p.lng]);
    });
    if (pts.length > 1) map.current.fitBounds(pts, { padding: [50, 50], maxZoom: 9 });
    else if (pts.length === 1) map.current.setView(pts[0], 8);
    setTimeout(() => map.current && map.current.invalidateSize(), 120);
  }, [partners]);

  const focus = (p) => {
    if (!p.located || !map.current) return;
    map.current.setView([p.lat, p.lng], 10, { animate: true });
    markers.current[p.id]?.openPopup();
  };

  const located = partners.filter((p) => p.located);
  const missing = partners.filter((p) => !p.located);
  const byRegion = located.reduce((acc, p) => {
    const r = p.region || t("pmap.unknownRegion");
    (acc[r] = acc[r] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="partners-map-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MapPin className="text-blue-800" size={22} /> {t("pmap.title")}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{t("pmap.sub")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load(true)} disabled={refreshing} data-testid="partners-map-refresh">
          <RefreshCw size={14} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> {t("pmap.refresh")}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div ref={mapRef} style={{ height: 560 }} className="w-full rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-slate-50" data-testid="partners-map" />
          <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
            <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full bg-blue-900" /> {located.length} {t("pmap.located")}</span>
            {missing.length > 0 && <span className="flex items-center gap-1.5 text-amber-600"><AlertCircle size={14} /> {missing.length} {t("pmap.notLocated")}</span>}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 max-h-[560px] overflow-y-auto" data-testid="partners-map-list">
            {loading ? (
              <div className="text-slate-400 py-8 text-center text-sm">…</div>
            ) : partners.length === 0 ? (
              <div className="text-slate-400 py-8 text-center text-sm" data-testid="partners-map-empty">{t("pmap.empty")}</div>
            ) : (
              <div className="space-y-5">
                {Object.entries(byRegion).sort((a, b) => a[0].localeCompare(b[0])).map(([region, list]) => (
                  <div key={region}>
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center justify-between">
                      <span>{region}</span><span className="text-slate-300">{list.length}</span>
                    </div>
                    <div className="space-y-1.5">
                      {list.map((p) => (
                        <button key={p.id} onClick={() => focus(p)} data-testid="partner-map-item"
                          className="w-full text-left rounded-lg border border-slate-100 hover:border-blue-200 hover:bg-blue-50/40 px-3 py-2 transition-colors">
                          <div className="font-medium text-slate-800 text-sm flex items-center gap-1.5"><Building2 size={13} className="text-slate-400" /> {p.name}</div>
                          {p.city && <div className="text-xs text-slate-500 mt-0.5 pl-5">{p.city}</div>}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {missing.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-amber-500 mb-2">{t("pmap.notLocated")}</div>
                    <div className="space-y-1.5">
                      {missing.map((p) => (
                        <div key={p.id} className="rounded-lg border border-amber-100 bg-amber-50/40 px-3 py-2" data-testid="partner-map-missing">
                          <div className="font-medium text-slate-800 text-sm">{p.name}</div>
                          <div className="text-xs text-amber-600 mt-0.5">{t("pmap.addAddress")}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
